import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  Plus, GitBranch, Edit2, Trash2, ChevronDown, ChevronUp, ChevronRight,
  X, Mail, Star, Eye, Copy, Download, Upload,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "../components/ui/collapsible";
import { Checkbox } from "../components/ui/checkbox";
import { Filter } from "lucide-react";
import {
  TaskEditor, DEFAULT_TASK, REFERENCE_DATES,
} from "../components/workflows/TaskEditor";

// Dynamic variables that can be inserted into email templates
/* ── Main Workflows Page ── */
const Workflows = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const isPlatformAdmin = user?.is_platform_admin === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sideFilter, setSideFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [expandedWorkflows, setExpandedWorkflows] = useState({});
  const [activeUsers, setActiveUsers] = useState([]);
  const [showCopyTaskDialog, setShowCopyTaskDialog] = useState(false);
  const [copyTaskSource, setCopyTaskSource] = useState(null); // { template_id, task_ids }
  const [copyTarget, setCopyTarget] = useState({ template_id: "", group_id: "" });
  const [showCopyGroupDialog, setShowCopyGroupDialog] = useState(false);
  const [copyGroupSource, setCopyGroupSource] = useState(null); // { template_id, group_id, group_name }
  const [copyGroupTarget, setCopyGroupTarget] = useState("");
  const [contacts, setContacts] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    property_type: "single_family",
    transaction_side: "buyer",
    is_starter_template: false,
    task_groups: [],
  });

  const fetchWorkflows = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workflows`, { withCredentials: true });
      setWorkflows(response.data);
      const expanded = {};
      response.data.forEach((wf) => { expanded[wf.template_id] = false; });
      setExpandedWorkflows(expanded);
    } catch {
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const [usersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setActiveUsers(usersRes.data);
      setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
    } catch (err) { toast.error("Failed to load users"); }
  }, []);

  useEffect(() => {
    fetchWorkflows();
    fetchUsers();
    if (searchParams.get("action") === "new") {
      setIsModalOpen(true);
      searchParams.delete("action");
      setSearchParams(searchParams);
    }
  }, [fetchWorkflows, fetchUsers, searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWorkflow) {
        const res = await axios.put(`${API}/workflows/${editingWorkflow.template_id}`, formData, { withCredentials: true });
        if (res.data?.pending_approval) {
          toast.success("Change request submitted for admin approval");
        } else {
          toast.success("Workflow updated");
        }
      } else {
        await axios.post(`${API}/workflows`, formData, { withCredentials: true });
        toast.success("Workflow created");
      }
      setIsModalOpen(false);
      resetForm();
      fetchWorkflows();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save workflow");
    }
  };

  const handleEdit = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      name: workflow.name,
      description: workflow.description || "",
      property_type: workflow.property_type,
      transaction_side: workflow.transaction_side,
      is_starter_template: workflow.is_starter_template || false,
      task_groups: (workflow.task_groups || []).map((g) => ({
        ...g,
        tasks: (g.tasks || []).map((t) => {
          const merged = { ...DEFAULT_TASK, ...t };
          // Convert legacy signed days_offset to new format
          if (!t.due_direction && merged.days_offset < 0) {
            merged.days_offset = Math.abs(merged.days_offset);
            merged.due_direction = "before";
          } else if (!t.due_direction && merged.days_offset > 0) {
            merged.due_direction = "after";
          }
          return merged;
        }),
      })),
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;
    try {
      await axios.delete(`${API}/workflows/${workflowToDelete.template_id}`, { withCredentials: true });
      toast.success("Workflow deleted");
      setShowDeleteDialog(false);
      setWorkflowToDelete(null);
      fetchWorkflows();
    } catch {
      toast.error("Failed to delete workflow");
    }
  };

  const moveWorkflow = async (index, direction) => {
    const newList = [...workflows];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newList.length) return;
    [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
    setWorkflows(newList);
    // Persist order
    const orderedIds = newList.map((w) => w.template_id);
    try {
      await axios.put(`${API}/workflows/reorder`, { order: orderedIds }, { withCredentials: true });
    } catch {
      toast.error("Failed to save order");
      fetchWorkflows();
    }
  };

  const resetForm = () => {
    setEditingWorkflow(null);
    setFormData({
      name: "",
      description: "",
      property_type: "single_family",
      transaction_side: "buyer",
      is_starter_template: false,
      task_groups: [],
    });
  };

  const handleDuplicate = async (workflow) => {
    try {
      await axios.post(`${API}/workflows/${workflow.template_id}/duplicate`, {}, { withCredentials: true });
      toast.success(`Workflow "${workflow.name}" duplicated`);
      fetchWorkflows();
    } catch {
      toast.error("Failed to duplicate workflow");
    }
  };

  const handleExportAll = async () => {
    try {
      const res = await axios.post(`${API}/workflows/export-token`, {}, { withCredentials: true });
      const url = `${API}/workflows/export?token=${res.data.token}`;
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 30000);
    } catch {
      toast.error("Failed to export workflows");
    }
  };

  const handleExportSingle = async (workflow) => {
    try {
      const res = await axios.post(`${API}/workflows/export-token`, { template_ids: workflow.template_id }, { withCredentials: true });
      const url = `${API}/workflows/export?token=${res.data.token}&template_ids=${workflow.template_id}`;
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 30000);
    } catch {
      toast.error("Failed to export workflow");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/workflows/import`, formData, { withCredentials: true });
      toast.success(res.data.message);
      fetchWorkflows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import workflows");
    }
    e.target.value = "";
  };

  const openCopyTaskDialog = (sourceTemplateId, taskId) => {
    setCopyTaskSource({ template_id: sourceTemplateId, task_ids: [taskId] });
    setCopyTarget({ template_id: "", group_id: "" });
    setShowCopyTaskDialog(true);
  };

  const handleCopyTasks = async () => {
    if (!copyTaskSource || !copyTarget.template_id || !copyTarget.group_id) return;
    try {
      const res = await axios.post(
        `${API}/workflows/${copyTaskSource.template_id}/copy-tasks`,
        { task_ids: copyTaskSource.task_ids, target_template_id: copyTarget.template_id, target_group_id: copyTarget.group_id },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      setShowCopyTaskDialog(false);
      setCopyTaskSource(null);
      fetchWorkflows();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy task");
    }
  };

  const openCopyGroupDialog = (sourceTemplateId, groupId, groupName) => {
    setCopyGroupSource({ template_id: sourceTemplateId, group_id: groupId, group_name: groupName });
    setCopyGroupTarget("");
    setShowCopyGroupDialog(true);
  };

  const handleCopyGroup = async () => {
    if (!copyGroupSource || !copyGroupTarget) return;
    try {
      const res = await axios.post(
        `${API}/workflows/${copyGroupSource.template_id}/copy-group/${copyGroupSource.group_id}`,
        { target_template_id: copyGroupTarget },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      setShowCopyGroupDialog(false);
      setCopyGroupSource(null);
      fetchWorkflows();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy group");
    }
  };

  const addTaskGroup = () => {
    setFormData({
      ...formData,
      task_groups: [
        ...formData.task_groups,
        {
          group_id: `group_${Date.now()}`,
          name: "New Task Group",
          tasks: [],
          order: formData.task_groups.length,
        },
      ],
    });
  };

  const updateTaskGroup = (index, updates) => {
    const newGroups = [...formData.task_groups];
    newGroups[index] = { ...newGroups[index], ...updates };
    setFormData({ ...formData, task_groups: newGroups });
  };

  const removeTaskGroup = (index) => {
    setFormData({
      ...formData,
      task_groups: formData.task_groups.filter((_, i) => i !== index),
    });
  };

  const [collapsedGroups, setCollapsedGroups] = useState({});
  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const moveGroup = (index, direction) => {
    setFormData(prev => {
      const groups = [...prev.task_groups];
      const target = index + direction;
      if (target < 0 || target >= groups.length) return prev;
      [groups[index], groups[target]] = [groups[target], groups[index]];
      return { ...prev, task_groups: groups };
    });
  };

  const addTask = (groupIndex) => {
    const newGroups = [...formData.task_groups];
    newGroups[groupIndex].tasks.push({
      ...DEFAULT_TASK,
      task_id: `task_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    });
    setFormData({ ...formData, task_groups: newGroups });
  };

  const updateTask = useCallback((groupIndex, taskIndex, updates) => {
    setFormData((prev) => {
      const newGroups = [...prev.task_groups];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        tasks: newGroups[groupIndex].tasks.map((t, i) =>
          i === taskIndex ? { ...t, ...updates } : t
        ),
      };
      return { ...prev, task_groups: newGroups };
    });
  }, []);

  const removeTask = useCallback((groupIndex, taskIndex) => {
    setFormData((prev) => {
      const newGroups = [...prev.task_groups];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        tasks: newGroups[groupIndex].tasks.filter((_, i) => i !== taskIndex),
      };
      return { ...prev, task_groups: newGroups };
    });
  }, []);

  const moveTask = useCallback((groupIndex, taskIndex, direction) => {
    setFormData((prev) => {
      const newGroups = [...prev.task_groups];
      const tasks = [...newGroups[groupIndex].tasks];
      const targetIndex = taskIndex + direction;
      if (targetIndex < 0 || targetIndex >= tasks.length) return prev;
      [tasks[taskIndex], tasks[targetIndex]] = [tasks[targetIndex], tasks[taskIndex]];
      newGroups[groupIndex] = { ...newGroups[groupIndex], tasks };
      return { ...prev, task_groups: newGroups };
    });
  }, []);

  // Flatten all tasks for dependency selection
  const allTasks = formData.task_groups.flatMap((g) => g.tasks);

  const filteredWorkflows = useMemo(() =>
    workflows.filter((w) => sideFilter === "all" || w.transaction_side === sideFilter),
    [workflows, sideFilter]
  );

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)] tracking-tight">
              Workflow Templates
            </h1>
            <p className="text-[var(--t-text-muted)] mt-1">Create reusable checklists for your transactions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { resetForm(); setIsModalOpen(true); }}
              className="btn-primary"
              data-testid="new-workflow-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Workflow
            </Button>
            <Button variant="outline" onClick={handleExportAll} data-testid="export-all-btn">
              <Download className="w-4 h-4 mr-2" />
              Export All
            </Button>
            <label htmlFor="import-file">
              <Button variant="outline" asChild data-testid="import-btn">
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </span>
              </Button>
            </label>
            <input id="import-file" type="file" accept=".json" className="hidden" onChange={handleImport} />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-[var(--t-text-muted)]" />
        <Select value={sideFilter} onValueChange={setSideFilter}>
          <SelectTrigger className="w-48" data-testid="side-filter-dropdown">
            <SelectValue placeholder="Filter by side" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Workflows</SelectItem>
            <SelectItem value="buyer">Buyer Side</SelectItem>
            <SelectItem value="seller">Seller Side</SelectItem>
            <SelectItem value="dual">Dual Side</SelectItem>
          </SelectContent>
        </Select>
        {sideFilter !== "all" && (
          <span className="text-xs text-[var(--t-text-muted)]">
            {filteredWorkflows.length} of {workflows.length} workflows
          </span>
        )}
      </div>

      {/* Workflows List */}
      {workflows.length > 0 ? (
        <div className="space-y-4" data-testid="workflows-list">
          {filteredWorkflows
            .map((workflow) => {
            const index = workflows.indexOf(workflow);
            return (
            <Card key={workflow.template_id} className="card-surface" data-testid={`workflow-item-${index}`}>
              <Collapsible
                open={expandedWorkflows[workflow.template_id]}
                onOpenChange={(open) => setExpandedWorkflows({ ...expandedWorkflows, [workflow.template_id]: open })}
              >
                <CardHeader className="py-4 px-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-slate-100 rounded-md">
                        <GitBranch className="w-6 h-6 text-[var(--t-primary)]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-[var(--t-text)]">{workflow.name}</h3>
                        {workflow.description && (
                          <p className="text-sm text-[var(--t-text-muted)] mt-1">{workflow.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant="outline" className="text-xs capitalize">
                            {workflow.property_type.replace("_", " ")}
                          </Badge>
                          <Badge variant="outline" className="text-xs capitalize">
                            {workflow.transaction_side} Side
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {workflow.task_groups?.reduce((acc, g) => acc + (g.tasks?.length || 0), 0) || 0} tasks
                          </Badge>
                          {workflow.task_groups?.some((g) => g.tasks?.some((t) => t.task_type === "email")) && (
                            <Badge variant="outline" className="text-xs border-blue-200 text-blue-600">
                              <Mail className="w-3 h-3 mr-1" /> Has Emails
                            </Badge>
                          )}
                          {isPlatformAdmin && workflow.is_starter_template && (
                            <Badge variant="outline" className="text-xs border-amber-200 text-amber-700 bg-amber-50">
                              <Star className="w-3 h-3 mr-1" /> Starter Template
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="flex flex-col mr-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-6 p-0 text-[var(--t-text-subtle)] hover:text-[var(--t-text)]"
                          onClick={(e) => { e.stopPropagation(); moveWorkflow(index, -1); }}
                          disabled={index === 0}
                          data-testid={`move-up-${index}`}
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-6 p-0 text-[var(--t-text-subtle)] hover:text-[var(--t-text)]"
                          onClick={(e) => { e.stopPropagation(); moveWorkflow(index, 1); }}
                          disabled={index === workflows.length - 1}
                          data-testid={`move-down-${index}`}
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(workflow)} data-testid={`edit-workflow-${index}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(workflow)} title="Duplicate workflow" data-testid={`duplicate-workflow-${index}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleExportSingle(workflow)} title="Export workflow" data-testid={`export-workflow-${index}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => { setWorkflowToDelete(workflow); setShowDeleteDialog(true); }}
                          data-testid={`delete-workflow-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm">
                          {expandedWorkflows[workflow.template_id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent className="pt-0 px-5 pb-5">
                    {workflow.task_groups?.map((group) => (
                      <div key={group.group_id} className="mb-4 last:mb-0">
                        <p className="text-sm font-semibold text-[var(--t-text)] mb-2">{group.name}</p>
                        <div className="space-y-2 pl-4 border-l-2 border-[var(--t-border)]">
                          {group.tasks?.map((task) => {
                            const isDep = task.depends_on_task_id && group.tasks.some((t) => t.task_id === task.depends_on_task_id);
                            return (
                            <div key={task.task_id} className={`group/task flex items-center gap-3 text-sm ${isDep ? "ml-6 pl-3 border-l-2 border-dashed border-slate-300" : ""}`}>
                              {task.task_type === "email" ? (
                                <Mail className="w-3 h-3 text-blue-500 shrink-0" />
                              ) : (
                                <span className="w-2 h-2 rounded-full bg-slate-300 shrink-0"></span>
                              )}
                              <span className="text-[var(--t-text)]">{task.title}</span>
                              {task.is_key_date && <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" />}
                              <span className="text-[var(--t-text-subtle)] text-xs">
                                ({(() => {
                                  const offset = task.days_offset || 0;
                                  const dir = task.due_direction || (offset < 0 ? "before" : offset > 0 ? "after" : "before");
                                  const absOffset = Math.abs(offset);
                                  return `${absOffset}d ${dir} `;
                                })()}
                                {REFERENCE_DATES.find((r) => r.value === task.reference_date)?.label || task.reference_date}
                                {task.use_business_days ? " BD" : ""})
                              </span>
                              {(task.assigned_parties || []).length > 0 && (
                                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                                  {(task.assigned_parties || []).length} assigned
                                </Badge>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); openCopyTaskDialog(workflow.template_id, task.task_id); }}
                                className="ml-auto opacity-0 group-hover/task:opacity-100 text-[var(--t-text-subtle)] hover:text-[var(--t-primary)] transition-opacity p-1 rounded hover:bg-[var(--t-bg)]"
                                title="Copy task to another workflow"
                                data-testid={`copy-task-${task.task_id}`}
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            );
                          })}
                          {(!group.tasks || group.tasks.length === 0) && (
                            <p className="text-sm text-[var(--t-text-subtle)] italic">No tasks in this group</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!workflow.task_groups || workflow.task_groups.length === 0) && (
                      <p className="text-sm text-[var(--t-text-subtle)] italic">No task groups defined</p>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
          })}
        </div>
      ) : (
        <Card className="card-surface">
          <CardContent className="py-12">
            <div className="empty-state">
              <GitBranch className="empty-state-icon" />
              <p className="empty-state-title">No workflows yet</p>
              <p className="empty-state-description">
                Create your first workflow template to streamline your transactions.
              </p>
              <Button
                onClick={() => { resetForm(); setIsModalOpen(true); }}
                className="btn-primary mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Workflow
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="workflow-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold [font-family:var(--t-heading-font)]">
              {editingWorkflow ? "Edit Workflow" : "New Workflow"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="form-group">
                <Label htmlFor="name">Workflow Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Buyer Side - Single Family"
                  required
                  data-testid="input-workflow-name"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe when to use this workflow..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Property Type</Label>
                  <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single_family">Single Family</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="townhouse">Townhouse</SelectItem>
                      <SelectItem value="multi_family">Multi-Family</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label>Transaction Side</Label>
                  <Select value={formData.transaction_side} onValueChange={(v) => setFormData({ ...formData, transaction_side: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">Buyer Side</SelectItem>
                      <SelectItem value="seller">Seller Side</SelectItem>
                      <SelectItem value="dual">Dual Agency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Starter Template Toggle — Platform Admin Only */}
              {isPlatformAdmin && (
                <div className="flex items-center gap-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                  <Checkbox
                    id="is_starter_template"
                    checked={formData.is_starter_template}
                    onCheckedChange={(v) => setFormData({ ...formData, is_starter_template: !!v })}
                    data-testid="starter-template-checkbox"
                  />
                  <div>
                    <Label htmlFor="is_starter_template" className="text-sm font-medium cursor-pointer text-amber-900">
                      Include in Starter Templates
                    </Label>
                    <p className="text-xs text-amber-700 mt-0.5">
                      When checked, this workflow will be automatically cloned to new tenants on signup. Unchecked workflows stay private to your org only.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Task Groups */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Task Groups</Label>
                <Button type="button" variant="outline" size="sm" onClick={addTaskGroup} data-testid="add-task-group-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Group
                </Button>
              </div>

              {formData.task_groups.map((group, gIndex) => (
                <Card key={group.group_id} className="border border-[var(--t-border)]">
                  <CardHeader className="py-3 px-4 bg-[var(--t-bg)] cursor-pointer" onClick={() => toggleGroupCollapse(group.group_id)}>
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-5 w-6 p-0 text-[var(--t-text-subtle)] hover:text-[var(--t-text)]"
                          onClick={(e) => { e.stopPropagation(); moveGroup(gIndex, -1); }}
                          disabled={gIndex === 0}
                          data-testid={`move-group-up-${gIndex}`}
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-5 w-6 p-0 text-[var(--t-text-subtle)] hover:text-[var(--t-text)]"
                          onClick={(e) => { e.stopPropagation(); moveGroup(gIndex, 1); }}
                          disabled={gIndex === formData.task_groups.length - 1}
                          data-testid={`move-group-down-${gIndex}`}
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {collapsedGroups[group.group_id]
                        ? <ChevronRight className="w-4 h-4 text-[var(--t-text-subtle)] shrink-0" />
                        : <ChevronDown className="w-4 h-4 text-[var(--t-text-subtle)] shrink-0" />
                      }
                      <Input
                        value={group.name}
                        onChange={(e) => updateTaskGroup(gIndex, { name: e.target.value })}
                        className="flex-1 h-8 text-sm font-medium"
                        placeholder="Group name..."
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`task-group-name-${gIndex}`}
                      />
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {group.tasks.length} task{group.tasks.length !== 1 ? "s" : ""}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-[var(--t-text-subtle)] hover:text-[var(--t-text)]"
                        onClick={(e) => { e.stopPropagation(); openCopyGroupDialog(editingWorkflow?.template_id || formData.template_id, group.group_id, group.name); }}
                        title="Copy group to another workflow"
                        data-testid={`copy-group-${gIndex}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={(e) => { e.stopPropagation(); removeTaskGroup(gIndex); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  {!collapsedGroups[group.group_id] && (
                  <CardContent className="p-3 space-y-2">
                    {group.tasks.map((task, tIndex) => (
                      <TaskEditor
                        key={task.task_id}
                        task={task}
                        groupIndex={gIndex}
                        taskIndex={tIndex}
                        taskCount={group.tasks.length}
                        allTasks={allTasks}
                        activeUsers={activeUsers}
                        currentUser={user}
                        contacts={contacts}
                        onUpdate={updateTask}
                        onRemove={removeTask}
                        onMove={moveTask}
                      />
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => addTask(gIndex)}
                      data-testid={`add-task-btn-${gIndex}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Task
                    </Button>
                  </CardContent>
                  )}
                </Card>
              ))}

              {formData.task_groups.length === 0 && (
                <p className="text-sm text-[var(--t-text-subtle)] text-center py-4">
                  No task groups yet. Click "Add Group" to create your first task group.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-workflow">
                {editingWorkflow ? "Update Workflow" : "Create Workflow"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-workflow-dialog">
          <DialogHeader>
            <DialogTitle>Delete Workflow</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{workflowToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-workflow">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Task to Workflow Dialog */}
      <Dialog open={showCopyTaskDialog} onOpenChange={(open) => { if (!open) { setShowCopyTaskDialog(false); setCopyTaskSource(null); } }}>
        <DialogContent data-testid="copy-task-dialog">
          <DialogHeader>
            <DialogTitle>Copy Task to Another Workflow</DialogTitle>
            <DialogDescription>
              Select a target workflow and task group to copy this task into.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1.5 block">Target Workflow</Label>
              <Select
                value={copyTarget.template_id}
                onValueChange={(v) => setCopyTarget({ template_id: v, group_id: "" })}
              >
                <SelectTrigger data-testid="copy-target-workflow">
                  <SelectValue placeholder="Select workflow..." />
                </SelectTrigger>
                <SelectContent>
                  {workflows
                    .filter((w) => w.template_id !== copyTaskSource?.template_id)
                    .map((w) => (
                      <SelectItem key={w.template_id} value={w.template_id}>{w.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {copyTarget.template_id && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1.5 block">Target Task Group</Label>
                <Select
                  value={copyTarget.group_id}
                  onValueChange={(v) => setCopyTarget({ ...copyTarget, group_id: v })}
                >
                  <SelectTrigger data-testid="copy-target-group">
                    <SelectValue placeholder="Select task group..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(workflows.find((w) => w.template_id === copyTarget.template_id)?.task_groups || []).map((g) => (
                      <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCopyTaskDialog(false); setCopyTaskSource(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCopyTasks}
              disabled={!copyTarget.template_id || !copyTarget.group_id}
              className="btn-primary"
              data-testid="confirm-copy-task"
            >
              <Copy className="w-4 h-4 mr-1" />
              Copy Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Group Dialog */}
      <Dialog open={showCopyGroupDialog} onOpenChange={(open) => { if (!open) { setShowCopyGroupDialog(false); setCopyGroupSource(null); } }}>
        <DialogContent data-testid="copy-group-dialog">
          <DialogHeader>
            <DialogTitle>Copy Task Group</DialogTitle>
            <DialogDescription>
              Copy "{copyGroupSource?.group_name}" and all its tasks to another workflow.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Target Workflow</Label>
              <Select value={copyGroupTarget} onValueChange={setCopyGroupTarget}>
                <SelectTrigger data-testid="copy-group-target-workflow">
                  <SelectValue placeholder="Select workflow..." />
                </SelectTrigger>
                <SelectContent>
                  {workflows
                    .filter((w) => w.template_id !== copyGroupSource?.template_id)
                    .map((w) => (
                      <SelectItem key={w.template_id} value={w.template_id}>{w.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCopyGroupDialog(false); setCopyGroupSource(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCopyGroup}
              disabled={!copyGroupTarget}
              className="btn-primary"
              data-testid="confirm-copy-group"
            >
              <Copy className="w-4 h-4 mr-1" />
              Copy Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Workflows;
